/**
 * One track-record item — create / edit / attach / verify. A dialog rather
 * than a second sidebar because the workbench lives inside the borrower
 * sidebar already.
 *
 * Styling: this is the reference implementation of FORM_STYLE_GUIDE.md — every
 * size, weight, gap and color comes from `theme/formStyles.js` (`FORM`,
 * `formSx`, `formLabel`). Sections are 40px apart with a hairline; fields 24px;
 * label → control → helper glued at 6px.
 *
 *   1 The property      address (geolocate autofill → the model's `Address`
 *                       object: fullAddress, streetNumber/Name, city, state,
 *                       zip, county, lat/lng) · experience type · resolution
 *   2 The numbers       DYNAMIC on the resolution — the backend infers
 *                       `resolutionStatus` from the numbers (experience-workbook.md
 *                       §"populate": sale date/price → Sold; held + rent →
 *                       Retained as Rental; held → In Progress) and
 *                       `profit_estimate` needs sale date + price + purchase price
 *                       on a Sold row, so the form asks for exactly the fields the
 *                       chosen resolution implies and derives `isCurrentlyHeld`.
 *                       `borrowerEntityMembershipPercent` ("the percent ownership
 *                       of the borrower in the entity") shows once an entity is
 *                       credited.
 *   3 Credits           borrowers / entities / GCs it credits — the portability
 *                       targets; an item with none persists NOWHERE on verify
 *   4 Proof             loan documents that evidence it — Experience and Track
 *                       Record group first, then HUD / deed / property-record
 *                       hints, the rest folded away; upload new proof inline
 *   5 Count · verify    (edit only) countedForExperience toggle; Verify /
 *                       Un-verify with the persistence result rendered
 *
 * Server-owned fields (`verified`, `verifiedBy`, `verifiedDate`, audit stamps)
 * are never sent; verification only moves through `/verify`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import UploadOutlined from "@mui/icons-material/UploadOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import {
  FormDocumentGroup,
  FormComboField as Combo,
  FormField as Field,
  FormFooter,
  FormMoneyField as Money,
  FormSection as Section,
  FormSectionDivider as SectionDivider,
  FormTextField as Text,
  FormVerifiedToggle,
  FormWarn as Warn,
} from "../../../components/forms/FormPrimitives";
import AddressGeolocateInput from "../../../components/inputs/AddressGeolocateInput";
import LoanBorrowerFileUploadProcess from "../../../components/processes/LoanBorrowerFileUploadProcess";
import { usePreviewOverDialog } from "../../../hooks/usePreviewOverDialog";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { formatAddressObject } from "../../../_src_shared/utils/functions/formatAddressObject";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { status } from "../../../theme/colors";
import { FORM, formSx, px } from "../../../theme/formStyles";
import {
  ASSOCIATION_TYPES,
  docName,
  EXPERIENCE_DOC_GROUP,
  EXPERIENCE_TYPES,
  fmtPoints,
  itemAddress,
  listLoanParties,
  proofRank,
  QUALIFYING_CATEGORIES,
  QUALIFYING_CATEGORY_POINTS,
  RESOLUTION_STATUSES,
  sortProofDocs,
  SOURCES,
} from "./experienceConstants";
import PersistenceNotice from "./PersistenceNotice";
import ExperienceCreditsPicker from "./ExperienceCreditsPicker";
import { isDocHidden } from "../../../_src_shared/utils/functions/isDocHidden";
/* ────────────────────────────── parties ─────────────────────────────────── */

/* ───────────────────────── resolution → field rules ─────────────────────── */
/**
 * Which numbers a resolution needs. Mirrors the backend's own inference of
 * `resolutionStatus` from the numbers (sale date/price → Sold; held + rent →
 * Retained as Rental; held → In Progress) and `profit_estimate`'s inputs.
 * `helper` replaces the section helper while that resolution is chosen.
 */
const RESOLUTION_RULES = {
  Sold: {
    held: false,
    sale: true,
    heldEconomics: false,
    rent: false,
    helper:
      "Sold — a clear disposition. Sale date and price drive the profit estimate: sale price minus (purchase + renovation budget) × 1.05.",
  },
  "Retained as Rental": {
    held: true,
    sale: false,
    heldEconomics: true,
    rent: true,
    helper:
      "Retained as rental — still held, producing income. Rent plus the current mortgage and value describe it; no sale fields.",
  },
  "In Progress": {
    held: true,
    sale: false,
    heldEconomics: true,
    rent: false,
    helper:
      "Still held — project in progress, no disposition. Sale fields aren't needed.",
  },
};

// ISO datetime ↔ the date input's YYYY-MM-DD (date-only; the calendar fields).
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");
const fromDateInput = (s) => (s ? `${s}T00:00:00` : null);

// The stored `propertyAddress` is the model's `Address` object. Rows written
// before the autofill (or seeded from a schedule) may carry parts but no
// `fullAddress` — synthesize one so the picker can show the selection.
const toAddressForm = (a) => {
  if (!a || typeof a !== "object") return null;
  if (a.fullAddress) return a;
  const s = formatAddressObject(a);
  return s && s !== "N/A" ? { ...a, fullAddress: s } : null;
};

// Strip the picker's UI-only keys; everything else is `Address` (or a
// backend-set extra such as `geolocation` / `apn`) and goes up unchanged.
const toAddressBody = (a) => {
  if (!a || typeof a !== "object") return null;
  const { id, coordinateTuple, score, countryCode, ...rest } = a;
  return rest;
};

const toForm = (item, defaults) => ({
  propertyAddress: toAddressForm(item?.propertyAddress),
  experienceType: item?.experienceType || null,
  resolutionStatus: item?.resolutionStatus || null,
  qualifyingCategory: item?.qualifyingCategory || null,
  unitCount: item?.unitCount > 1 ? String(item.unitCount) : "",
  experienceNotes: item?.experienceNotes || "",
  propertyPurchaseDate: toDateInput(item?.propertyPurchaseDate),
  propertyPurchasePrice: item?.propertyPurchasePrice ?? null,
  propertySaleDate: toDateInput(item?.propertySaleDate),
  propertySalePrice: item?.propertySalePrice ?? null,
  propertyRenovationBudget: item?.propertyRenovationBudget ?? null,
  borrowerEntityMembershipPercent:
    item?.borrowerEntityMembershipPercent ?? null,
  mortgageBalance: item?.mortgageBalance ?? null,
  arvValue: item?.arvValue ?? null,
  grossEquity: item?.grossEquity ?? null,
  monthlyRent: item?.monthlyRent ?? null,
  lenderName: item?.lenderName || "",
  isCurrentlyHeld: item?.isCurrentlyHeld ?? null,
  groupingLabel: item?.groupingLabel || "",
  countedForExperience: item?.countedForExperience !== false,
  borrowers: (item?.associations?.borrowers || defaults?.borrowers || []).map(
    String,
  ),
  entities: (item?.associations?.entities || []).map(String),
  generalContractors: (item?.associations?.generalContractors || []).map(
    String,
  ),
  loanDocuments: (item?.associations?.loanDocuments || []).map(String),
});

const toBody = (f) => {
  const rule = RESOLUTION_RULES[f.resolutionStatus];
  const showSale = rule ? rule.sale : true;
  const showHeld = rule ? rule.heldEconomics : true;
  const showRent = rule ? rule.rent : true;
  const showMembership = f.entities.length > 0;
  return {
    propertyAddress: toAddressBody(f.propertyAddress),
    experienceType: f.experienceType || null,
    resolutionStatus: f.resolutionStatus || null,
    qualifyingCategory: f.qualifyingCategory || null,
    // Blank, 0 or 1 = one unit; only a real multi-unit count goes up.
    unitCount: Math.floor(Number(f.unitCount)) > 1 ? Math.floor(Number(f.unitCount)) : null,
    experienceNotes: f.experienceNotes.trim() || null,
    propertyPurchaseDate: fromDateInput(f.propertyPurchaseDate),
    propertyPurchasePrice: f.propertyPurchasePrice ?? null,
    // Fields the resolution doesn't need go up null so a switch from Sold →
    // In Progress doesn't leave a phantom sale behind.
    propertySaleDate: showSale ? fromDateInput(f.propertySaleDate) : null,
    propertySalePrice: showSale ? (f.propertySalePrice ?? null) : null,
    propertyRenovationBudget: f.propertyRenovationBudget ?? null,
    borrowerEntityMembershipPercent:
      !showMembership ||
      f.borrowerEntityMembershipPercent === "" ||
      f.borrowerEntityMembershipPercent == null
        ? null
        : Number(f.borrowerEntityMembershipPercent),
    mortgageBalance: showHeld ? (f.mortgageBalance ?? null) : null,
    arvValue: showHeld ? (f.arvValue ?? null) : null,
    grossEquity: showHeld ? (f.grossEquity ?? null) : null,
    monthlyRent: showRent ? (f.monthlyRent ?? null) : null,
    lenderName: showHeld ? f.lenderName || null : null,
    // Derived from the resolution (the backend reads it the same way); a row
    // with no resolution keeps whatever the schedule said.
    isCurrentlyHeld: rule ? rule.held : (f.isCurrentlyHeld ?? null),
    groupingLabel: f.groupingLabel || null,
    countedForExperience: !!f.countedForExperience,
    associations: {
      borrowers: f.borrowers,
      entities: f.entities,
      generalContractors: f.generalContractors,
      loanDocuments: f.loanDocuments,
      subjectProperties: [],
    },
  };
};

/** What's still missing before a NEW item can be added. */
const missingFields = (f) => {
  const rule = RESOLUTION_RULES[f.resolutionStatus];
  const out = [];
  if (!f.propertyAddress?.fullAddress) out.push("address");
  if (!f.experienceType) out.push("experience type");
  if (!f.resolutionStatus) out.push("resolution");
  // Required because an unclassified row silently counts a FULL point — the
  // asterisk has to gate, or it is just decoration.
  if (!f.qualifyingCategory) out.push("qualifying category");
  if (!f.propertyPurchaseDate) out.push("purchase date");
  if (f.propertyPurchasePrice == null) out.push("purchase price");
  if (rule?.sale) {
    if (!f.propertySaleDate) out.push("sale date");
    if (f.propertySalePrice == null) out.push("sale price");
  }
  if (!(f.borrowers.length + f.entities.length + f.generalContractors.length))
    out.push("at least one credit");
  return out;
};

const MEMBERSHIP_LABEL = "Borrower's membership in the entity (%)";

/* ─────────────────────────────── dialog ────────────────────────────────── */

const ExperienceItemDialog = ({
  open,
  onClose,
  item,
  loan,
  defaultBorrowerId,
  busy,
  onCreate,
  onUpdate,
  onVerify,
  onRemove,
  onExtract,
}) => {
  const { dialogProps: previewSafeDialogProps } = usePreviewOverDialog(onClose);
  const isEdit = !!item?._id;
  const defaults = useMemo(
    () => ({ borrowers: defaultBorrowerId ? [defaultBorrowerId] : [] }),
    [defaultBorrowerId],
  );
  const [f, setF] = useState(() => toForm(item, defaults));
  const [docQuery, setDocQuery] = useState("");
  const [showOtherDocs, setShowOtherDocs] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [persistence, setPersistence] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // Doors this one address counts for (blank / 0 / 1 = one).
  const units = Math.max(1, Math.floor(Number(f.unitCount)) || 1);

  useEffect(() => {
    if (!open) return;
    setF(toForm(item, defaults));
    setPersistence(null);
    setConfirmRemove(false);
    setDocQuery("");
    setShowOtherDocs(false);
    setUploadOpen(false);
  }, [open, item, defaults]);

  const parties = useMemo(() => listLoanParties(loan), [loan]);
  // Who this item most likely belongs to: the borrower it was opened from,
  // and an entity named like the schedule section (holding entity) it sat under.
  const suggestedIds = useMemo(() => {
    const label = (f.groupingLabel || "").trim().toLowerCase();
    return [
      ...(defaultBorrowerId ? [String(defaultBorrowerId)] : []),
      ...parties
        .filter((p) => label && p.type === ASSOCIATION_TYPES.ENTITIES && p.name.trim().toLowerCase() === label)
        .map((p) => p.id),
    ];
  }, [defaultBorrowerId, parties, f.groupingLabel]);
  const docs = useMemo(
    () => sortProofDocs(loan?.loanDocuments || []),
    [loan?.loanDocuments],
  );
  const docsById = useMemo(
    () => new Map(docs.map((d) => [String(d._id), d])),
    [docs],
  );

  // A document uploaded from the Proof section lands on the loan; when the
  // loan's document list grows while the upload panel is open, the new ids
  // are attached as proof automatically.
  const knownDocIds = useRef(null);
  useEffect(() => {
    const ids = new Set(docs.map((d) => String(d._id)));
    if (knownDocIds.current && uploadOpen) {
      const fresh = [...ids].filter((id) => !knownDocIds.current.has(id));
      if (fresh.length)
        setF((p) => ({
          ...p,
          loanDocuments: [...new Set([...p.loanDocuments, ...fresh])],
        }));
    }
    knownDocIds.current = ids;
  }, [docs, uploadOpen]);

  const q = docQuery.trim().toLowerCase();
  // Hidden documents are not offered as proof. One that was already linked
  // when the dialog opened stays listed, so it can still be seen and unticked.
  const linkedAtOpen = useMemo(
    () => new Set((item?.associations?.loanDocuments || []).map(String)),
    [item],
  );
  const docGroups = useMemo(() => {
    const matches = (d) =>
      (!isDocHidden(d) || linkedAtOpen.has(String(d._id))) &&
      (!q ||
      `${docName(d)} ${d.docType || ""} ${d.docGroup || ""}`
        .toLowerCase()
        .includes(q));
    return {
      primary: docs.filter((d) => proofRank(d) === 0 && matches(d)),
      records: docs.filter((d) => proofRank(d) === 1 && matches(d)),
      other: docs.filter((d) => proofRank(d) === 2 && matches(d)),
    };
  }, [docs, q, linkedAtOpen]);

  const toggleIn = (key, id) =>
    setF((p) => ({
      ...p,
      [key]: p[key].includes(String(id))
        ? p[key].filter((x) => x !== String(id))
        : [...p[key], String(id)],
    }));

  const parentCount =
    f.borrowers.length + f.entities.length + f.generalContractors.length;
  const busyHere = busy === "create" || (isEdit && busy === item._id);
  const rule = RESOLUTION_RULES[f.resolutionStatus] || null;
  const showMembership = f.entities.length > 0;
  const missing = missingFields(f);
  const canAdd = isEdit || missing.length === 0;

  /**
   * Per-section completeness, computed from the OPENED record (not the live
   * form) so a section collapses on open and then stays where the user left
   * it. An edit of an already-filled item therefore starts with the finished
   * sections closed and only the unfinished ones on screen; a new item starts
   * fully open.
   */
  const initial = useMemo(
    () => (isEdit ? toForm(item, defaults) : null),
    [isEdit, item, defaults],
  );
  const done = useMemo(() => {
    if (!initial) return {};
    const r = RESOLUTION_RULES[initial.resolutionStatus];
    return {
      property:
        !!initial.propertyAddress?.fullAddress &&
        !!initial.experienceType &&
        !!initial.resolutionStatus,
      numbers:
        !!r &&
        !!initial.propertyPurchaseDate &&
        initial.propertyPurchasePrice != null &&
        (!r.sale ||
          (!!initial.propertySaleDate && initial.propertySalePrice != null)),
      credits:
        initial.borrowers.length +
          initial.entities.length +
          initial.generalContractors.length >
        0,
      proof: initial.loanDocuments.length > 0,
    };
  }, [initial]);

  /**
   * Live completeness — drives the create-time auto-collapse on the sections
   * whose "done" is a set of CHOICES (property identity, credits, proof), so
   * picking the last one folds the section and moves you on. Section 2 is left
   * out: its fields are typed, and folding mid-keystroke would fight the user.
   */
  const liveDone = {
    property:
      !!f.propertyAddress?.fullAddress &&
      !!f.experienceType &&
      !!f.resolutionStatus,
    credits: parentCount > 0,
    proof: f.loanDocuments.length > 0,
  };

  const save = async () => {
    const body = toBody(f);
    if (isEdit) await onUpdate(item._id, body);
    else await onCreate(body);
    onClose();
  };

  // Add this one and start the next with the same credits — the schedule
  // rows for one borrower come in runs.
  const saveAndNext = async () => {
    await onCreate(toBody(f));
    setF((p) => ({
      ...toForm(null, defaults),
      borrowers: p.borrowers,
      entities: p.entities,
      generalContractors: p.generalContractors,
      groupingLabel: p.groupingLabel,
    }));
    setDocQuery("");
    setUploadOpen(false);
  };

  const verify = async (verified) => {
    // Save any edits first so the persisted item is what the reviewer sees.
    if (isEdit) {
      await onUpdate(item._id, toBody(f));
      const p = await onVerify(item._id, verified);
      setPersistence({ verified, result: p });
    }
  };

  const src = SOURCES[item?.source] || null;
  const firstBorrowerId = f.borrowers[0] || defaultBorrowerId || null;

  const docGroup = (title, rows, { pill } = {}) => (
    <FormDocumentGroup
      title={title}
      pill={pill}
      docs={rows}
      selectedIds={f.loanDocuments}
      onToggle={(id) => toggleIn("loanDocuments", id)}
    />
  );

  return (
    <Dialog
      open={open}
      {...previewSafeDialogProps}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: formSx.paper }}
    >
      {/* header — pinned */}
      <Box sx={formSx.head}>
        <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Txt sx={formSx.title}>
              {isEdit ? "Experience item" : "New experience item"}
            </Txt>
            <Txt sx={formSx.titleHelper}>
              {isEdit
                ? [
                    src ? `${src.label}` : null,
                    item?.verified
                      ? `Verified by ${item.verifiedBy || "—"} · ${formatDateTime(item.verifiedDate, "datetime") || ""}`
                      : "Candidate — not yet verified",
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : "A property the borrower, entity or contractor did — with who it credits and what proves it."}
            </Txt>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{ mt: px(-4), mr: px(-8) }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
        {!isEdit && onExtract ? (
          <Flx g={1.5} sx={{ mt: px(FORM.s.s3), alignItems: "flex-start" }}>
            <DescriptionOutlined
              sx={{ fontSize: 16, color: FORM.color.ink4, mt: px(1) }}
            />
            <Txt sx={{ ...formSx.meta, color: FORM.color.ink2 }}>
              Adding several from a schedule or closing statement?{" "}
              <Link
                component="button"
                type="button"
                onClick={onExtract}
                sx={{ ...formSx.link, fontSize: FORM.type.meta[0] }}
              >
                Extract the document instead
              </Link>{" "}
              — it seeds every row and attaches the proof for you.
            </Txt>
          </Flx>
        ) : null}
      </Box>

      {/* body — scrolls */}
      <DialogContent sx={formSx.shellBody}>
        {/* 1 · property */}
        <Section
          n={1}
          title="Property"
          helper="What was done, and where."
          collapsible
          defaultCollapsed={isEdit && done.property}
          autoCollapseWhen={isEdit ? undefined : liveDone.property}
          summary={[itemAddress(item), f.experienceType, f.resolutionStatus]
            .filter((x) => x && x !== "—")
            .join(" · ")}
        >
          <Box sx={formSx.fields}>
            <Field
              label="Property address"
              required
              helperText="Start typing and pick a match — it fills street, city, state, ZIP, county and coordinates."
            >
              <AddressGeolocateInput
                value={f.propertyAddress}
                onChange={(a) => set("propertyAddress", a || null)}
                placeholder="1551 SE 23rd Ave, Ocala, FL"
                fullWidth
                selectedVariant="input"
                sx={formSx.input}
              />
            </Field>
            {/* What it was and how it ended, then what it's worth: two rows of
                two. The guideline bucket (Qualifying category) decides the
                points: an income-producing purchase and third-party GC work
                count half, everything else a full one, times the units sold.
                The seed pre-classifies from type + resolution; this is where
                the reviewer confirms it. An unclassified row still counts 1.0,
                so leaving it blank is a silent full point rather than a no-op. */}
            <Box sx={formSx.grid2}>
              <Combo
                label="Experience type"
                required
                options={EXPERIENCE_TYPES}
                value={f.experienceType}
                onChange={(v) => set("experienceType", v)}
              />
              <Combo
                label="Resolution"
                required
                options={RESOLUTION_STATUSES}
                value={f.resolutionStatus}
                onChange={(v) => set("resolutionStatus", v)}
                helperText="Decides which numbers you'll see."
              />
            </Box>
            <Box sx={formSx.grid2}>
              <Combo
                label="Qualifying category"
                required
                options={QUALIFYING_CATEGORIES}
                value={f.qualifyingCategory}
                onChange={(v) => set("qualifyingCategory", v)}
                helperText={
                  f.qualifyingCategory
                    ? `Counts ${fmtPoints(
                        QUALIFYING_CATEGORY_POINTS[f.qualifyingCategory] * units,
                      )} toward the experience rollup${units > 1 ? ` (${fmtPoints(QUALIFYING_CATEGORY_POINTS[f.qualifyingCategory])} × ${units} units)` : ""}.`
                    : `Not classified — counts ${fmtPoints(units)} until you pick one.`
                }
              />
              {/* Door count: one address sold unit by unit counts once per unit. */}
              <Text
                label={
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    Units sold individually
                    <Tooltip
                      title="Only when each unit was sold on its own, like a condo sell-out with one deed per unit. A building sold to one buyer in one closing stays at 1."
                    >
                      <InfoOutlined
                        aria-label="About units sold individually"
                        sx={{ fontSize: 14, color: FORM.color.ink4, cursor: "help" }}
                      />
                    </Tooltip>
                  </Box>
                }
                type="number"
                value={f.unitCount}
                onChange={(v) => set("unitCount", v.replace(/[^0-9]/g, ""))}
                placeholder="0"
                inputProps={{ min: 0, step: 1, inputMode: "numeric" }}
                helperText="Only if each unit sold separately."
              />
            </Box>
            <Text
              label="Notes"
              multiline
              minRows={3}
              value={f.experienceNotes}
              onChange={(v) => set("experienceNotes", v)}
              placeholder="e.g. 5-unit condo conversion, all units sold in 2024, deeds attached"
              helperText="Why this item counts the way it does."
            />
            {units > 1 && !f.experienceNotes.trim() ? (
              <Warn>
                This item counts {units} units. Add a note explaining the count.
              </Warn>
            ) : null}
          </Box>
        </Section>

        <SectionDivider />

        {/* 2 · numbers — shaped by the resolution */}
        <Section
          n={2}
          title="The numbers"
          helper={
            rule
              ? rule.helper
              : "As shown on the schedule or the closing statement."
          }
          collapsible
          defaultCollapsed={isEdit && done.numbers}
          summary={[
            formatDollar(f.propertyPurchasePrice, null),
            f.propertySalePrice != null
              ? `→ ${formatDollar(f.propertySalePrice)}`
              : null,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {!rule ? (
            <Box sx={formSx.note}>
              Pick a resolution above — only the fields that resolution needs
              will appear here.
            </Box>
          ) : (
            <Box sx={formSx.fields}>
              {/* One continuous two-column grid so the conditional fields fill
                  rows in order — bought · sold · renovation · held economics ·
                  membership · schedule section — with no holes. */}
              <Box sx={formSx.grid2}>
                <Text
                  label="Purchase date"
                  type="date"
                  required
                  value={f.propertyPurchaseDate}
                  onChange={(v) => set("propertyPurchaseDate", v)}
                />
                <Money
                  label="Purchase price"
                  required
                  value={f.propertyPurchasePrice}
                  onChange={(v) => set("propertyPurchasePrice", v)}
                />

                {rule.sale ? (
                  <>
                    <Text
                      label="Sale date"
                      type="date"
                      required
                      value={f.propertySaleDate}
                      onChange={(v) => set("propertySaleDate", v)}
                    />
                    <Money
                      label="Sale price"
                      required
                      value={f.propertySalePrice}
                      onChange={(v) => set("propertySalePrice", v)}
                    />
                  </>
                ) : null}

                <Money
                  label="Renovation budget"
                  value={f.propertyRenovationBudget}
                  onChange={(v) => set("propertyRenovationBudget", v)}
                  helperText={
                    rule.sale
                      ? "Without it the profit estimate runs on purchase alone (overstated)."
                      : undefined
                  }
                />

                {rule.heldEconomics ? (
                  <>
                    <Money
                      label="Mortgage balance"
                      value={f.mortgageBalance}
                      onChange={(v) => set("mortgageBalance", v)}
                    />
                    <Money
                      label="ARV / current value"
                      value={f.arvValue}
                      onChange={(v) => set("arvValue", v)}
                    />
                    <Money
                      label="Gross equity (as printed)"
                      value={f.grossEquity}
                      onChange={(v) => set("grossEquity", v)}
                    />
                    {rule.rent ? (
                      <Money
                        label="Monthly rent"
                        value={f.monthlyRent}
                        onChange={(v) => set("monthlyRent", v)}
                      />
                    ) : null}
                    <Text
                      label="Lender"
                      value={f.lenderName}
                      onChange={(v) => set("lenderName", v)}
                      placeholder="e.g. Ridgeline Capital"
                    />
                  </>
                ) : null}

                {showMembership ? (
                  <Text
                    label={MEMBERSHIP_LABEL}
                    value={f.borrowerEntityMembershipPercent}
                    onChange={(v) => set("borrowerEntityMembershipPercent", v)}
                    placeholder="100"
                  />
                ) : null}

                <Text
                  label="Schedule section (holding entity)"
                  value={f.groupingLabel}
                  onChange={(v) => set("groupingLabel", v)}
                  placeholder="e.g. James Oliva LLC"
                  helperText="The header the row sat under on the track-record schedule."
                />
              </Box>

              {item?.profitEstimate && rule.sale ? (
                <Box
                  sx={{
                    ...formSx.note,
                    borderStyle: "solid",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "baseline",
                    gap: px(FORM.s.s2),
                  }}
                >
                  <span>Estimated profit</span>
                  <Box
                    component="span"
                    sx={{
                      ...formSx.body,
                      ...formSx.money,
                      fontWeight: 600,
                      color:
                        Number(item.profitEstimate.estimatedProfit) < 0
                          ? status.error.fg
                          : status.success.fg,
                    }}
                  >
                    {formatDollar(item.profitEstimate.estimatedProfit, "—")}
                  </Box>
                  <Box component="span" sx={formSx.money}>
                    on{" "}
                    {formatDollar(item.profitEstimate.estimatedAllInBasis, "—")}{" "}
                    all-in ·{" "}
                    {item.profitEstimate.estimatedReturn != null
                      ? `${(item.profitEstimate.estimatedReturn * 100).toFixed(1)}%`
                      : "—"}
                  </Box>
                  <Box component="span" sx={formSx.meta}>
                    {item.profitEstimate.estimateNote}
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}
        </Section>

        <SectionDivider />

        {/* 3 · credits */}
        <Section
          n={3}
          title="Credits"
          helper="Who gets this on their record. Once it's verified, checked parties keep it on future loans."
          collapsible
          defaultCollapsed={isEdit && done.credits}
          // No auto-collapse: credits are often several picks, so the section stays open while choosing.
          summary={parentCount ? `${parentCount} credited` : "No parent yet"}
        >
          {parties.length ? (
            <ExperienceCreditsPicker
              parties={parties}
              value={{
                borrowers: f.borrowers,
                entities: f.entities,
                generalContractors: f.generalContractors,
              }}
              onChange={(type, ids) => set(type, ids)}
              suggestedIds={suggestedIds}
            />
          ) : (
            <Box sx={formSx.note}>
              No borrowers, entities or contractors on the loan yet.
            </Box>
          )}
          {!parentCount ? (
            <Box sx={{ mt: px(FORM.s.s4) }}>
              <Warn>
                <strong>No parent yet.</strong> On verify, this item counts on
                the deal but persists to nobody's record. Pick who it belongs
                to.
              </Warn>
            </Box>
          ) : null}
        </Section>

        <SectionDivider />

        {/* 4 · proof */}
        <Section
          n={4}
          title="Proof"
          helper="The documents that evidence it."
          collapsible
          defaultCollapsed={isEdit && done.proof}
          autoCollapseWhen={isEdit ? undefined : liveDone.proof}
          summary={
            f.loanDocuments.length
              ? `${f.loanDocuments.length} document${f.loanDocuments.length === 1 ? "" : "s"} attached`
              : "No proof attached"
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: px(FORM.s.s4),
            }}
          >
            <TextField
              size="small"
              fullWidth
              value={docQuery}
              onChange={(e) => setDocQuery(e.target.value)}
              placeholder="Search loan documents…"
              sx={formSx.input}
              InputProps={{
                startAdornment: (
                  <SearchOutlined sx={{ fontSize: 18, mr: px(8) }} />
                ),
              }}
            />

            {docs.length ? (
              <>
                {docGroups.primary.length
                  ? docGroup("Experience and track record", docGroups.primary, {
                      pill: "Sorted first",
                    })
                  : null}
                {docGroups.records.length
                  ? docGroup("Settlement & property records", docGroups.records)
                  : null}
                {docGroups.other.length ? (
                  showOtherDocs || q ? (
                    docGroup("Other loan documents", docGroups.other)
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      onClick={() => setShowOtherDocs(true)}
                      sx={{ ...formSx.link, alignSelf: "flex-start" }}
                    >
                      Show {docGroups.other.length} other loan document
                      {docGroups.other.length === 1 ? "" : "s"} (non-experience
                      types)
                    </Link>
                  )
                ) : null}
                {q &&
                !docGroups.primary.length &&
                !docGroups.records.length &&
                !docGroups.other.length ? (
                  <Box sx={formSx.note}>
                    No documents match "{docQuery.trim()}".
                  </Box>
                ) : null}
              </>
            ) : (
              <Box sx={formSx.note}>No loan documents uploaded yet.</Box>
            )}

            {/* upload new proof — lands in the Experience and Track Record group and is attached on arrival */}
            {loan?._id ? (
              uploadOpen ? (
                <Box sx={{ ...formSx.note, bgcolor: FORM.color.surface }}>
                  <Flx ac jb sx={{ mb: px(FORM.s.s2) }}>
                    <Txt sx={{ ...formSx.label, mb: 0 }}>Upload new proof</Txt>
                    <Button
                      size="small"
                      variant="text"
                      color="gray"
                      onClick={() => setUploadOpen(false)}
                    >
                      Done
                    </Button>
                  </Flx>
                  <LoanBorrowerFileUploadProcess
                    loanId={loan._id}
                    borrowerId={firstBorrowerId}
                    docGroup={EXPERIENCE_DOC_GROUP}
                    showDivider={false}
                    keyName="experience-proof-upload"
                  />
                </Box>
              ) : (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  sx={{
                    ...formSx.note,
                    display: "flex",
                    alignItems: "center",
                    gap: px(FORM.s.s3),
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    "&:hover": {
                      bgcolor: FORM.color.surface3,
                      borderColor: FORM.color.ink4,
                    },
                  }}
                >
                  <UploadOutlined
                    sx={{ fontSize: 18, color: FORM.color.ink4 }}
                  />
                  Upload new proof — a HUD-1, deed, or property record not yet
                  on the loan
                </Box>
              )
            ) : null}

            {f.loanDocuments.length ? (
              <Box sx={formSx.chipRow}>
                {f.loanDocuments.map((id) => (
                  <Chip
                    key={id}
                    size="small"
                    label={docName(docsById.get(id))}
                    onDelete={() => toggleIn("loanDocuments", id)}
                    sx={{ ...formSx.meta, height: 24 }}
                  />
                ))}
              </Box>
            ) : isEdit ? (
              <Warn>
                <strong>No proof attached.</strong> An unevidenced item can't be
                trusted on verify. Attach the closing statement, deed or
                property record first.
              </Warn>
            ) : null}
          </Box>
        </Section>

        {/* 5 · count · verify — an item has to exist before it can be verified */}
        {isEdit ? (
          <>
            <SectionDivider />
            <Section
              n={5}
              title="Count and verify"
              helper="Verification is the gate to the rollup and the shared record."
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: px(FORM.s.s4),
                }}
              >
                <Flx ac g={1}>
                  <Switch
                    size="small"
                    checked={!!f.countedForExperience}
                    onChange={(e) =>
                      set("countedForExperience", e.target.checked)
                    }
                  />
                  <Box>
                    <Txt sx={{ ...formSx.label, mb: 0, display: "block" }}>
                      Counted toward experience
                    </Txt>
                    <Txt sx={{ ...formSx.helper, mt: 0, display: "block" }}>
                      Untick to exclude from the rollup without deleting the
                      row.
                    </Txt>
                  </Box>
                </Flx>
                <FormVerifiedToggle
                  verified={!!item.verified}
                  verifiedText="Counts on the deal and is persisted to the parents' canonical records with its proof."
                  candidateText="Does not count and is not persisted until verified."
                  action={
                    <Tooltip
                      title={
                        !item.verified && !parentCount
                          ? "Attach a parent first — otherwise it persists nowhere"
                          : ""
                      }
                    >
                      <span>
                        <Button
                          variant={item.verified ? "outlined" : "contained"}
                          color={item.verified ? "gray" : "primary"}
                          onClick={() => verify(!item.verified)}
                          disabled={busyHere}
                          sx={formSx.button}
                        >
                          {item.verified ? "Un-verify" : "Save & verify"}
                        </Button>
                      </span>
                    </Tooltip>
                  }
                />
                {persistence ? (
                  <PersistenceNotice
                    verified={persistence.verified}
                    result={persistence.result}
                  />
                ) : null}
              </Box>
            </Section>
          </>
        ) : null}
      </DialogContent>

      {/* footer — pinned: status left, actions right */}
      <FormFooter
        status={
          isEdit ? (
            confirmRemove ? (
              <Flx ac g={1} wrap>
                <span>Remove this item?</span>
                <Button
                  color="error"
                  variant="contained"
                  disabled={busyHere}
                  onClick={async () => {
                    await onRemove(item._id);
                    onClose();
                  }}
                  sx={formSx.button}
                >
                  Remove
                </Button>
                <Button
                  variant="text"
                  color="gray"
                  onClick={() => setConfirmRemove(false)}
                  sx={formSx.button}
                >
                  Keep
                </Button>
              </Flx>
            ) : (
              <Button
                variant="text"
                color="error"
                onClick={() => setConfirmRemove(true)}
                sx={{ ...formSx.button, ml: px(-FORM.s.s4) }}
              >
                Remove item
              </Button>
            )
          ) : (
            <>
              Saves as an <strong>unverified</strong> item — nothing counts
              toward experience until underwriting verifies it.
            </>
          )
        }
      >
        <Button
          variant="text"
          color="gray"
          onClick={onClose}
          disabled={busyHere}
          sx={formSx.button}
        >
          Cancel
        </Button>
        {!isEdit ? (
          <Button
            variant="outlined"
            color="gray"
            onClick={saveAndNext}
            disabled={busyHere || !canAdd}
            sx={formSx.button}
          >
            Add &amp; next
          </Button>
        ) : null}
        <Button
          onClick={save}
          disabled={busyHere || !canAdd}
          sx={formSx.button}
        >
          {isEdit ? "Save" : "Add item"}
        </Button>
      </FormFooter>
    </Dialog>
  );
};

ExperienceItemDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  item: PropTypes.object,
  loan: PropTypes.object,
  defaultBorrowerId: PropTypes.string,
  busy: PropTypes.any,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  onVerify: PropTypes.func,
  onRemove: PropTypes.func,
  /** Opens the schedule / closing-statement extract flow instead (create mode only). */
  onExtract: PropTypes.func,
};

export default ExperienceItemDialog;
